// Shared registration field catalog used by the admin Page Builder and the public form.

export const STANDARD_OPTIONS = Array.from({ length: 12 }, (_, i) => `Standard ${i + 1}`);

export const FIELD_CATALOG = [
  { key: "student_name", label: "Student Full Name", type: "text", group: "Student" },
  { key: "dob", label: "Date of Birth", type: "date", group: "Student" },
  { key: "gender", label: "Gender", type: "select", options: ["Male", "Female", "Other"], group: "Student" },
  { key: "standard", label: "Standard / Class", type: "select", options: STANDARD_OPTIONS, group: "Student" },
  { key: "division", label: "Division", type: "text", group: "Student" },
  { key: "roll_number", label: "Roll Number", type: "number", group: "Student" },
  { key: "school_name", label: "School Name", type: "text", group: "Student" },
  { key: "parent_name", label: "Parent / Guardian Name", type: "text", group: "Parent" },
  { key: "mobile", label: "Mobile (WhatsApp)", type: "tel", group: "Parent" },
  { key: "email", label: "Email", type: "email", group: "Parent" },
  { key: "emergency_contact", label: "Emergency Contact", type: "tel", group: "Parent" },
  { key: "address", label: "Address", type: "textarea", group: "Additional" },
  { key: "city", label: "City", type: "text", group: "Additional" },
  { key: "state", label: "State", type: "text", group: "Additional" },
  { key: "notes", label: "Additional Notes", type: "textarea", group: "Additional" },
];

export const FIELD_TYPES = ["text", "number", "date", "tel", "email", "textarea", "select", "checkbox"];

// Custom field types offered in the builder.
export const CUSTOM_FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "tel", label: "Phone (10-digit)" },
  { value: "select", label: "Dropdown" },
  { value: "checkbox", label: "Checkbox" },
];

// Sensible starting form for a brand-new page. Nothing is "core"/locked — the admin can
// toggle Required (which shows + requires) or turn a field off (hidden) at any time.
const DEFAULT_KEYS = ["student_name", "parent_name", "mobile", "standard", "email"];
const REQUIRED_DEFAULT = ["student_name", "parent_name", "mobile"];

export const DEFAULT_FIELDS = FIELD_CATALOG
  .filter((f) => DEFAULT_KEYS.includes(f.key))
  .map((f) => ({ ...f, required: REQUIRED_DEFAULT.includes(f.key) }));

export function makeCustomKey(label) {
  const base = (label || "field").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "").slice(0, 24) || "field";
  return `custom_${base}_${Math.random().toString(36).slice(2, 6)}`;
}

const NOT_STUDENT = /parent|father|mother|guardian|school|teacher|coordinator|principal|emergency|contact|user ?name/;

// Student name from whatever the form calls it: built-in field, First/Middle/Last, Full/Student/Child Name, or plain "Name".
export function studentNameFrom(fields, values) {
  const own = String(values?.student_name || "").trim();
  if (own) return own;
  const parts = { first: "", middle: "", last: "", full: "", plain: "" };
  (fields || []).forEach((f) => {
    const l = (f.label || f.key || "").toLowerCase();
    const v = String(values?.[f.key] ?? "").trim();
    if (!v || f.type === "checkbox" || !/name/.test(l) || NOT_STUDENT.test(l)) return;
    if (/first|given/.test(l)) parts.first = parts.first || v;
    else if (/middle/.test(l)) parts.middle = parts.middle || v;
    else if (/last|sur ?name|family/.test(l)) parts.last = parts.last || v;
    else if (/student|full|child|participant|candidate|ward|kid/.test(l)) parts.full = parts.full || v;
    else parts.plain = parts.plain || v;
  });
  const joined = [parts.first, parts.middle, parts.last].filter(Boolean).join(" ");
  return parts.full && !parts.first ? parts.full : joined || parts.full || parts.plain;
}

// Summary rows built only from what this page actually has; blanks are never shown.
export function summaryRows(page, fields, values) {
  const student = studentNameFrom(fields, values);
  const rows = [];
  const push = (k, v, id) => { const s = String(v ?? "").trim(); if (s) rows.push({ k, v: s, id }); };
  push("Student", student, "student");
  push("School", values?.school_name || page?.school_name, "school");
  push("Program", page?.program_name, "program");
  push("Level", page?.level_name, "level");
  push("Batch", page?.batch_name, "batch");
  (fields || []).forEach((f) => {
    if (f.type === "checkbox" || f.key === "student_name" || f.key === "school_name") return;
    const v = String(values?.[f.key] ?? "").trim();
    if (!v || (student && v === student)) return;
    const l = (f.label || "").toLowerCase();
    if (/name/.test(l) && !NOT_STUDENT.test(l) && student.split(" ").includes(v)) return;
    push(f.label || f.key, v, f.key);
  });
  return rows;
}
